import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import RichTextEditor from './RichTextEditor';

interface SocialMediaLinks {
  website: string;
  linkedin: string;
  twitter: string;
  instagram: string;
  facebook: string;
  youtube: string;
}

interface CardData {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  videoUrl: string;
  socialMedia?: SocialMediaLinks;
  position: number;
  expertise?: string[];
  interesses?: string[];
  talen?: string[];
  heroBackgrounds?: string[];
}

const DragDropCardsEditor: React.FC = () => {
  const [activeCard, setActiveCard] = useState('biografie');
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [dragOverCard, setDragOverCard] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [cardsData, setCardsData] = useState<Record<string, CardData>>({
    biografie: {
      id: 'biografie',
      title: 'Biografie',
      subtitle: 'Abdul Antonio',
      description: 'Beschrijf hier je biografie...',
      image: '',
      videoUrl: '',
      position: 0,
      expertise: ['Marketing', 'Strategy', 'Leadership'],
      interesses: ['Fotografie', 'Reizen', 'Muziek'],
      talen: ['Nederlands (Moedertaal)', 'Engels (Vloeiend)', 'Spaans (Basis)'],
      heroBackgrounds: []
    },
    'mijn-bedrijf': {
      id: 'mijn-bedrijf',
      title: 'Mijn bedrijf',
      subtitle: 'SuperKonnected',
      description: 'Beschrijf hier je bedrijf en wat jullie doen...',
      image: '',
      videoUrl: '',
      position: 1,
      socialMedia: {
        website: 'https://superkonnected.com',
        linkedin: 'https://linkedin.com/company/superkonnected',
        twitter: 'https://twitter.com/superkonnected',
        instagram: 'https://instagram.com/superkonnected',
        facebook: 'https://facebook.com/superkonnected',
        youtube: 'https://youtube.com/@superkonnected'
      }
    },
    expertise: {
      id: 'expertise',
      title: 'Expertise',
      subtitle: 'Connector - Authenticiteit',
      description: 'Beschrijf hier je professionele expertise en vaardigheden...',
      image: '',
      videoUrl: '',
      position: 2
    },
    activiteit: {
      id: 'activiteit',
      title: 'Activiteit',
      subtitle: '',
      description: 'Beschrijf hier je huidige activiteiten en projecten...',
      image: '',
      videoUrl: '',
      position: 3
    },
    'beschikbaar-voor': {
      id: 'beschikbaar-voor',
      title: 'Beschikbaar voor',
      subtitle: '',
      description: 'Beschrijf hier waar je beschikbaar voor bent en hoe mensen contact kunnen opnemen...',
      image: '',
      videoUrl: '',
      position: 4
    },
    'mijn-community': {
      id: 'mijn-community',
      title: 'Mijn Community/groep',
      subtitle: '',
      description: 'Beschrijf hier je community en groepen...',
      image: '',
      videoUrl: '',
      position: 5
    },
    'mijn-videos': {
      id: 'mijn-videos',
      title: 'Mijn Videos',
      subtitle: 'Video Content & Portfolio',
      description: 'Beschrijf hier je video portfolio, tutorials, en andere video content...',
      image: '',
      videoUrl: 'https://youtube.com/@superkonnected',
      position: 6,
      socialMedia: {
        youtube: 'https://youtube.com/@superkonnected',
        website: '',
        linkedin: '',
        twitter: '',
        instagram: '',
        facebook: ''
      }
    },
    mastermind: {
      id: 'mastermind',
      title: 'Mastermind',
      subtitle: '',
      description: 'Beschrijf hier je mastermind groep...',
      image: '',
      videoUrl: '',
      position: 7
    }
  });

  const [newExpertise, setNewExpertise] = useState('');
  const [newInteresse, setNewInteresse] = useState('');
  const [newTaal, setNewTaal] = useState('');

  const baseCardTypes = [
    { id: 'biografie', label: 'Biografie', emoji: '👤' },
    { id: 'mijn-bedrijf', label: 'Mijn bedrijf', emoji: '🏢' },
    { id: 'expertise', label: 'Expertise', emoji: '🎯' },
    { id: 'activiteit', label: 'Activiteit', emoji: '⚡' },
    { id: 'beschikbaar-voor', label: 'Beschikbaar voor', emoji: '🤝' },
    { id: 'mijn-community', label: 'Mijn Community/groep', emoji: '👥' },
    { id: 'mastermind', label: 'Mastermind', emoji: '🧠' },
    { id: 'custom', label: 'Custom Kaart', emoji: '📝' }
  ];

  // Load cards from Firebase
  const loadCardsFromFirebase = useCallback(async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.log('No user authenticated, using default cards');
        setLoading(false);
        return;
      }

      const db = getFirestore();
      const userDocRef = doc(db, 'userProfiles', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.cardsData && typeof userData.cardsData === 'object') {
          console.log('✅ Loaded cards data from Firebase:', userData.cardsData);
          setCardsData(userData.cardsData);
        }
      }
    } catch (error) {
      console.error('❌ Error loading cards from Firebase:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save cards to Firebase
  const saveCardsToFirebase = useCallback(async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.error('No user authenticated');
        return;
      }

      setSaving(true);
      const db = getFirestore();
      const userDocRef = doc(db, 'userProfiles', user.uid);
      
      await updateDoc(userDocRef, {
        cardsData: cardsData,
        lastUpdated: new Date()
      });
      
      console.log('✅ Cards saved to Firebase successfully');
      alert('Kaarten succesvol opgeslagen!');
      
      // Navigate back to Mijn Verhaal page
      setTimeout(() => {
        window.location.hash = '#mijn-verhaal';
      }, 1000); // Small delay to show the success message
      
    } catch (error) {
      console.error('❌ Error saving cards to Firebase:', error);
      alert('Er ging iets mis bij het opslaan. Probeer het opnieuw.');
    } finally {
      setSaving(false);
    }
  }, [cardsData]);

  // Upload image to Firebase Storage
  const handleImageUpload = useCallback(async (file: File, cardId: string) => {
    try {
      setUploadingImage(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.error('No user authenticated');
        return;
      }

      // Create storage reference
      const imageRef = ref(storage, `card-images/${user.uid}/${cardId}/${Date.now()}-${file.name}`);
      
      // Upload file
      const snapshot = await uploadBytes(imageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update card data with new image URL
      setCardsData(prev => ({
        ...prev,
        [cardId]: {
          ...prev[cardId],
          image: downloadURL
        }
      }));
      
      console.log('✅ Image uploaded successfully:', downloadURL);
      
    } catch (error: any) {
      console.error('❌ Error uploading image:', error);
      
      // More specific error messages
      let errorMessage = 'Er ging iets mis bij het uploaden van de afbeelding.';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Je hebt geen toestemming om bestanden te uploaden.';
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'Upload geannuleerd.';
      } else if (error.code === 'storage/unknown') {
        errorMessage = 'Onbekende fout bij uploaden. Controleer je internetverbinding.';
      } else if (error.code === 'storage/invalid-format') {
        errorMessage = 'Ongeldig bestandsformaat.';
      } else if (error.code === 'storage/object-not-found') {
        errorMessage = 'Bestand niet gevonden.';
      }
      
      alert(`${errorMessage} Probeer het opnieuw.`);
    } finally {
      setUploadingImage(false);
    }
  }, []);

  // Upload hero background image to Firebase Storage
  const handleHeroBackgroundUpload = useCallback(async (file: File | undefined, index: number) => {
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Bestand is te groot. Maximum is 10MB.');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Ongeldig bestandstype. Gebruik JPG, PNG, GIF of WebP.');
      return;
    }
    
    try {
      setUploadingImage(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.error('No user authenticated');
        alert('Je moet ingelogd zijn om afbeeldingen te uploaden.');
        return;
      }

      console.log('📤 Uploading hero background...', { index, fileName: file.name, fileSize: file.size });

      // Create storage reference for hero backgrounds
      const imageRef = ref(storage, `heroBackgrounds/${user.uid}/${Date.now()}-${file.name}`);
      
      // Upload file
      const snapshot = await uploadBytes(imageRef, file);
      console.log('✅ Upload complete, getting URL...');
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('✅ Hero background uploaded:', downloadURL);
      
      // Update biografie card with new hero background
      setCardsData(prev => {
        const currentBackgrounds = prev.biografie?.heroBackgrounds || [];
        const newBackgrounds = [...currentBackgrounds];
        newBackgrounds[index] = downloadURL;
        
        return {
          ...prev,
          biografie: {
            ...prev.biografie,
            heroBackgrounds: newBackgrounds
          }
        };
      });
      
    } catch (error: any) {
      console.error('❌ Error uploading hero background:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Er ging iets mis bij het uploaden.';
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Je hebt geen toestemming om bestanden te uploaden.';
      } else if (error.code === 'storage/unknown') {
        errorMessage = 'Upload fout. Check je internetverbinding en probeer opnieuw.';
      }
      
      alert(errorMessage);
    } finally {
      setUploadingImage(false);
    }
  }, []);

  // Remove hero background
  const removeHeroBackground = useCallback((index: number) => {
    setCardsData(prev => {
      const currentBackgrounds = prev.biografie?.heroBackgrounds || [];
      const newBackgrounds = [...currentBackgrounds];
      newBackgrounds[index] = '';
      
      return {
        ...prev,
        biografie: {
          ...prev.biografie,
          heroBackgrounds: newBackgrounds.filter(bg => bg !== '')
        }
      };
    });
  }, []);

  // Load cards on component mount
  useEffect(() => {
    loadCardsFromFirebase();
  }, [loadCardsFromFirebase]);

  const sortedCards = Object.values(cardsData).sort((a, b) => a.position - b.position);
  const tabs = sortedCards.map(card => {
    const baseType = baseCardTypes.find(type => type.id === card.id);
    return {
      id: card.id,
      label: baseType ? baseType.label : card.title,
      emoji: baseType ? baseType.emoji : '📝'
    };
  });

  const currentCard = cardsData[activeCard];

  const handleDragStart = (e: React.DragEvent, cardId: string) => {
    setDraggedCard(cardId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, cardId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCard(cardId);
  };

  const handleDragLeave = () => {
    setDragOverCard(null);
  };

  const handleDrop = (e: React.DragEvent, targetCardId: string) => {
    e.preventDefault();
    
    if (!draggedCard || draggedCard === targetCardId) {
      setDraggedCard(null);
      setDragOverCard(null);
      return;
    }

    const draggedCardData = cardsData[draggedCard];
    const targetCardData = cardsData[targetCardId];
    
    const newCardsData = { ...cardsData };
    newCardsData[draggedCard] = { ...draggedCardData, position: targetCardData.position };
    newCardsData[targetCardId] = { ...targetCardData, position: draggedCardData.position };
    
    setCardsData(newCardsData);
    setDraggedCard(null);
    setDragOverCard(null);
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDragOverCard(null);
  };

  const addNewCard = () => {
    const cardCount = Object.keys(cardsData).length;
    if (cardCount >= 8) {
      alert('Maximum van 8 kaarten bereikt!');
      return;
    }

    const newId = `custom-${Date.now()}`;
    const newCard: CardData = {
      id: newId,
      title: `Custom Kaart ${cardCount}`,
      subtitle: '',
      description: '',
      image: '',
      videoUrl: '',
      position: cardCount
    };

    setCardsData(prev => ({ ...prev, [newId]: newCard }));
    setActiveCard(newId);
  };

  const restoreDefaultCards = () => {
    const defaultCards: Record<string, CardData> = {
      biografie: {
        id: 'biografie',
        title: 'Biografie',
        subtitle: 'Abdul Antonio',
        description: 'Beschrijf hier je biografie...',
        image: '',
        videoUrl: '',
        position: 0,
        expertise: ['Marketing', 'Strategy', 'Leadership'],
        interesses: ['Fotografie', 'Reizen', 'Muziek'],
        talen: ['Nederlands (Moedertaal)', 'Engels (Vloeiend)', 'Spaans (Basis)']
      },
      'mijn-bedrijf': {
        id: 'mijn-bedrijf',
        title: 'Mijn bedrijf',
        subtitle: 'SuperKonnected',
        description: 'Beschrijf hier je bedrijf en wat jullie doen...',
        image: '',
        videoUrl: '',
        position: 1,
        socialMedia: {
          website: 'https://superkonnected.com',
          linkedin: 'https://linkedin.com/company/superkonnected',
          twitter: 'https://twitter.com/superkonnected',
          instagram: 'https://instagram.com/superkonnected',
          facebook: 'https://facebook.com/superkonnected',
          youtube: 'https://youtube.com/@superkonnected'
        }
      },
      expertise: {
        id: 'expertise',
        title: 'Expertise',
        subtitle: 'Connector - Authenticiteit',
        description: 'Beschrijf hier je professionele expertise en vaardigheden...',
        image: '',
        videoUrl: '',
        position: 2
      },
      activiteit: {
        id: 'activiteit',
        title: 'Activiteit',
        subtitle: '',
        description: 'Beschrijf hier je huidige activiteiten en projecten...',
        image: '',
        videoUrl: '',
        position: 3
      },
      'beschikbaar-voor': {
        id: 'beschikbaar-voor',
        title: 'Beschikbaar voor',
        subtitle: '',
        description: 'Beschrijf hier waar je beschikbaar voor bent en hoe mensen contact kunnen opnemen...',
        image: '',
        videoUrl: '',
        position: 4
      },
      'mijn-community': {
        id: 'mijn-community',
        title: 'Mijn Community/groep',
        subtitle: '',
        description: 'Beschrijf hier je community en groepen...',
        image: '',
        videoUrl: '',
        position: 5
      },
      'mijn-videos': {
        id: 'mijn-videos',
        title: 'Mijn Videos',
        subtitle: 'Video Content & Portfolio',
        description: 'Beschrijf hier je video portfolio, tutorials, en andere video content...',
        image: '',
        videoUrl: 'https://youtube.com/@superkonnected',
        position: 6,
        socialMedia: {
          youtube: 'https://youtube.com/@superkonnected',
          website: '',
          linkedin: '',
          twitter: '',
          instagram: '',
          facebook: ''
        }
      },
      mastermind: {
        id: 'mastermind',
        title: 'Mastermind',
        subtitle: '',
        description: 'Beschrijf hier je mastermind groep...',
        image: '',
        videoUrl: '',
        position: 7
      }
    };

    // Merge with existing cards, keeping existing data but adding missing default cards
    const mergedCards = { ...cardsData };
    let maxPosition = Math.max(...Object.values(cardsData).map(card => card.position), -1);

    Object.entries(defaultCards).forEach(([key, defaultCard]) => {
      if (!mergedCards[key]) {
        maxPosition++;
        mergedCards[key] = { ...defaultCard, position: maxPosition };
      }
    });

    setCardsData(mergedCards);
    alert('Standaard kaarten hersteld!');
  };

  const deleteCard = (cardId: string) => {
    // Prevent deletion of core cards
    if (['biografie', 'mijn-bedrijf', 'expertise'].includes(cardId)) {
      alert('Deze kaart kan niet worden verwijderd omdat het een kernkaart is.');
      return;
    }
    
    if (Object.keys(cardsData).length <= 1) {
      alert('Je moet minimaal 1 kaart hebben!');
      return;
    }
    
    if (window.confirm('Weet je zeker dat je deze kaart wilt verwijderen?')) {
      const newCardsData = { ...cardsData };
      delete newCardsData[cardId];
      
      const remaining = Object.values(newCardsData).sort((a, b) => a.position - b.position);
      remaining.forEach((card, index) => {
        newCardsData[card.id] = { ...card, position: index };
      });
      
      setCardsData(newCardsData);
      
      if (activeCard === cardId) {
        setActiveCard(remaining[0]?.id || 'biografie');
      }
    }
  };

  const updateCardData = (field: keyof CardData, value: any) => {
    setCardsData(prev => ({
      ...prev,
      [activeCard]: {
        ...prev[activeCard],
        [field]: value
      }
    }));
  };

  const updateSocialMedia = (platform: keyof SocialMediaLinks, value: string) => {
    if (activeCard !== 'mijn-bedrijf') return;
    
    setCardsData(prev => ({
      ...prev,
      [activeCard]: {
        ...prev[activeCard],
        socialMedia: {
          ...prev[activeCard].socialMedia!,
          [platform]: value
        }
      }
    }));
  };

  const addExpertise = () => {
    if (newExpertise.trim() && activeCard === 'biografie') {
      const currentExpertise = cardsData.biografie.expertise || [];
      updateCardData('expertise', [...currentExpertise, newExpertise.trim()]);
      setNewExpertise('');
    }
  };

  const removeExpertise = (index: number) => {
    if (activeCard === 'biografie') {
      const currentExpertise = cardsData.biografie.expertise || [];
      const newExpertiseList = currentExpertise.filter((_, i) => i !== index);
      updateCardData('expertise', newExpertiseList);
    }
  };

  const addInteresse = () => {
    if (newInteresse.trim() && activeCard === 'biografie') {
      const currentInteresses = cardsData.biografie.interesses || [];
      updateCardData('interesses', [...currentInteresses, newInteresse.trim()]);
      setNewInteresse('');
    }
  };

  const removeInteresse = (index: number) => {
    if (activeCard === 'biografie') {
      const currentInteresses = cardsData.biografie.interesses || [];
      const newInteressesList = currentInteresses.filter((_, i) => i !== index);
      updateCardData('interesses', newInteressesList);
    }
  };

  const addTaal = () => {
    if (newTaal.trim() && activeCard === 'biografie') {
      const currentTalen = cardsData.biografie.talen || [];
      updateCardData('talen', [...currentTalen, newTaal.trim()]);
      setNewTaal('');
    }
  };

  const removeTaal = (index: number) => {
    if (activeCard === 'biografie') {
      const currentTalen = cardsData.biografie.talen || [];
      const newTalenList = currentTalen.filter((_, i) => i !== index);
      updateCardData('talen', newTalenList);
    }
  };

  const getCardColor = (cardId: string) => {
    const colors: Record<string, string> = {
      'biografie': '#d2691e',
      'mijn-bedrijf': '#2e7d32',
      'expertise': '#1976d2',
      'activiteit': '#f57c00',
      'beschikbaar-voor': '#7b1fa2',
      'mijn-community': '#c2185b',
      'mastermind': '#5d4037'
    };
    return colors[cardId] || '#6c757d';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Kaarten laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-green-600 text-lg">✓</span>
          <span className="font-medium text-sm">
            Mijn Verhaal - Drag & Drop Editor ({Object.keys(cardsData).length}/8 kaarten)
          </span>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        
        {/* Carousel Representation */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Carousel Representatie</h2>
          
          {/* Exact same 3D Carousel as MijnVerhaal */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="relative bg-gray-50 py-4">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* 3D Carousel */}
                <div className="relative h-48 flex items-center justify-center" style={{ perspective: '1000px' }}>
                  <div className="relative w-80 h-48" style={{ transformStyle: 'preserve-3d' }}>
                    {sortedCards.map((card, index) => {
                      const currentSlideIndex = sortedCards.findIndex(c => c.id === activeCard);
                      const offset = index - currentSlideIndex;
                      const isActive = offset === 0;
                      
                      let transform = '';
                      let opacity = 0.3;
                      let scale = 0.8;
                      let zIndex = 1;

                      if (isActive) {
                        transform = 'translateZ(0px)';
                        opacity = 1;
                        scale = 1;
                        zIndex = 10;
                      } else if (offset === 1) {
                        transform = `translateZ(-100px) translateX(150px) rotateY(-45deg)`;
                        opacity = 0.6;
                        scale = 0.9;
                        zIndex = 5;
                      } else if (offset === -1) {
                        transform = `translateZ(-100px) translateX(-150px) rotateY(45deg)`;
                        opacity = 0.6;
                        scale = 0.9;
                        zIndex = 5;
                      } else if (offset === 2) {
                        transform = `translateZ(-200px) translateX(300px) rotateY(-90deg)`;
                        opacity = 0.2;
                        scale = 0.7;
                      } else if (offset === -2) {
                        transform = `translateZ(-200px) translateX(-300px) rotateY(90deg)`;
                        opacity = 0.2;
                        scale = 0.7;
                      } else {
                        transform = `translateZ(-300px) translateX(${offset * 400}px)`;
                        opacity = 0;
                      }
                      
                      return (
                        <div
                          key={card.id}
                          className="absolute inset-0 transition-all duration-700 ease-out cursor-pointer"
                          style={{
                            transform,
                            opacity,
                            scale,
                            zIndex,
                            transformStyle: 'preserve-3d'
                          }}
                          onClick={() => setActiveCard(card.id)}
                        >
                          <div
                            className={`w-full h-full bg-gradient-to-br rounded-2xl shadow-lg flex flex-col items-center justify-center p-4 transform hover:scale-105 transition-transform duration-300 border border-gray-300 relative overflow-hidden`}
                            style={{
                              background: card.image 
                                ? `url(${card.image})` 
                                : `linear-gradient(135deg, ${getCardColor(card.id)} 0%, ${getCardColor(card.id)}88 100%)`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundBlendMode: card.image ? 'overlay' : undefined
                            }}
                          >
                            {/* Dark overlay for better text readability when image is present */}
                            {card.image && (
                              <div className="absolute inset-0 bg-black bg-opacity-40 rounded-2xl"></div>
                            )}
                            
                            <div className="relative z-10 flex flex-col items-center justify-center">
                              <div className="text-3xl mb-2">{tabs.find(tab => tab.id === card.id)?.emoji}</div>
                              <div className={`text-lg mb-1 font-bold text-center ${card.image ? 'text-white' : 'text-white'}`}>{card.title}</div>
                              <div className={`text-xs text-center ${card.image ? 'text-gray-200' : 'text-gray-200'}`}>{card.subtitle}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Navigation Arrows */}
                <button 
                  onClick={() => {
                    const currentIndex = sortedCards.findIndex(c => c.id === activeCard);
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : sortedCards.length - 1;
                    setActiveCard(sortedCards[prevIndex].id);
                  }}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-full transition-all duration-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={() => {
                    const currentIndex = sortedCards.findIndex(c => c.id === activeCard);
                    const nextIndex = currentIndex < sortedCards.length - 1 ? currentIndex + 1 : 0;
                    setActiveCard(sortedCards[nextIndex].id);
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-full transition-all duration-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Navigation Dots */}
                <div className="flex justify-center space-x-2 mt-4">
                  {sortedCards.map((card, index) => (
                    <button
                      key={card.id}
                      onClick={() => setActiveCard(card.id)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        card.id === activeCard ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Draggable Tabs - Now below the representation */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Kaarten Beheer</h3>
            <div className="flex gap-2">
              {Object.keys(cardsData).length < 8 && (
                <button
                  onClick={addNewCard}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <span>+</span>
                  Nieuwe Kaart
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm('Herstel alle standaard kaarten? Dit voegt ontbrekende standaard kaarten toe zonder bestaande kaarten te verwijderen.')) {
                    restoreDefaultCards();
                  }
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                title="Herstel ontbrekende standaard kaarten"
              >
                <span>🔄</span>
                Herstel Standaard Kaarten
              </button>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, tab.id)}
                  onDragOver={(e) => handleDragOver(e, tab.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, tab.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => setActiveCard(tab.id)}
                  className={`px-4 py-2 text-sm cursor-move transition-all duration-200 rounded-lg border-2 ${
                    activeCard === tab.id 
                      ? 'bg-blue-50 text-blue-700 border-blue-300 font-medium' 
                      : dragOverCard === tab.id 
                        ? 'bg-orange-100 text-orange-700 border-orange-300' 
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  } ${
                    draggedCard === tab.id ? 'opacity-50 border-dashed border-orange-400' : ''
                  }`}
                >
                  <span className="mr-2">{tab.emoji}</span>
                  {tab.label}
                  
                  {!['biografie', 'mijn-bedrijf', 'expertise'].includes(tab.id) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCard(tab.id);
                      }}
                      className="ml-2 bg-red-100 hover:bg-red-200 text-red-600 cursor-pointer text-xs px-1 py-0.5 rounded transition-colors"
                      title="Verwijder kaart"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Drag Instructions */}
            <div className="mt-3 p-3 bg-yellow-50 text-yellow-800 text-xs rounded-lg border border-yellow-200">
              🔄 <strong>Tip:</strong> Sleep de kaarten om de volgorde te wijzigen. Oranje kleur toont waar je de kaart neerzet.
            </div>
          </div>
        </div>

        {/* Current Card Details */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Bewerk: {tabs.find(tab => tab.id === activeCard)?.emoji} {currentCard.title}
          </h3>

          {/* Basis Informatie */}
          <div className="bg-white rounded-lg p-6 mb-4">
            <h3 className="text-lg font-semibold mb-5" style={{ color: getCardColor(activeCard) }}>
              {tabs.find(tab => tab.id === activeCard)?.emoji} Basis Informatie
            </h3>

            <div className="mb-5">
              <label className="block mb-1.5 font-medium text-sm">Titel</label>
              <input
                type="text"
                value={currentCard.title}
                onChange={(e) => updateCardData('title', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="mb-5">
              <label className="block mb-1.5 font-medium text-sm">Subtitel</label>
              <input
                type="text"
                value={currentCard.subtitle}
                onChange={(e) => updateCardData('subtitle', e.target.value)}
                placeholder="Subtitel (optioneel)"
                className="w-full px-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="mb-5">
              <label className="block mb-1.5 font-medium text-sm">Beschrijving</label>
              <RichTextEditor
                value={currentCard.description}
                onChange={(value) => updateCardData('description', value)}
                placeholder="Beschrijf hier je biografie..."
                className="w-full"
                style={{ minHeight: '120px' }}
              />
            </div>

            {/* Image Upload Section */}
            <div className="mb-5">
              <label className="block mb-1.5 font-medium text-sm">Afbeelding (optioneel)</label>
              
              {/* Current Image Preview */}
              {currentCard.image && (
                <div className="mb-3">
                  <div className="relative inline-block">
                    <img 
                      src={currentCard.image} 
                      alt="Huidige afbeelding" 
                      className="w-32 h-20 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      onClick={() => updateCardData('image', '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                      title="Afbeelding verwijderen"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Huidige afbeelding</p>
                </div>
              )}
              
              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Check file size (max 5MB)
                      if (file.size > 5 * 1024 * 1024) {
                        alert('Afbeelding is te groot. Maximaal 5MB toegestaan.');
                        return;
                      }
                      
                      // Check file type
                      if (!file.type.startsWith('image/')) {
                        alert('Alleen afbeeldingen zijn toegestaan.');
                        return;
                      }
                      
                      handleImageUpload(file, currentCard.id);
                    }
                  }}
                  className="hidden"
                  id={`image-upload-${currentCard.id}`}
                  disabled={uploadingImage}
                />
                <label 
                  htmlFor={`image-upload-${currentCard.id}`}
                  className={`cursor-pointer flex flex-col items-center ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploadingImage ? (
                    <>
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                      <p className="text-sm text-gray-600">Uploaden...</p>
                    </>
                  ) : (
                    <>
                      <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-sm text-gray-600">
                        {currentCard.image ? 'Klik om afbeelding te wijzigen' : 'Klik om afbeelding te uploaden'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF (max 5MB)</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="mb-5">
              <label className="block mb-1.5 font-medium text-sm">Video URL (optioneel)</label>
              <input
                type="url"
                value={currentCard.videoUrl}
                onChange={(e) => updateCardData('videoUrl', e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Social Media - Alleen voor Mijn Bedrijf */}
          {activeCard === 'mijn-bedrijf' && (
            <div className="bg-white rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold mb-5 text-cyan-600">
                🔗 Social Media Links
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'website', label: '🌐 Website', placeholder: 'https://jouwwebsite.nl' },
                  { key: 'linkedin', label: '💼 LinkedIn', placeholder: 'https://linkedin.com/in/jouwprofiel' },
                  { key: 'twitter', label: '🐦 Twitter', placeholder: 'https://twitter.com/jouwprofiel' },
                  { key: 'instagram', label: '📷 Instagram', placeholder: 'https://instagram.com/jouwprofiel' },
                  { key: 'facebook', label: '👥 Facebook', placeholder: 'https://facebook.com/jouwprofiel' },
                  { key: 'youtube', label: '📺 YouTube', placeholder: 'https://youtube.com/@jouwkanaal' }
                ].map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label className="block mb-1.5 font-medium text-sm">{label}</label>
                    <input
                      type="url"
                      value={currentCard.socialMedia?.[key as keyof SocialMediaLinks] || ''}
                      onChange={(e) => updateSocialMedia(key as keyof SocialMediaLinks, e.target.value)}
                      placeholder={placeholder}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hero Backgrounds - only for biografie */}
          {activeCard === 'biografie' && (
            <div className="bg-white rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold mb-3 text-indigo-600">
                🖼️ Hero Achtergrond (max 3 afbeeldingen)
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Deze afbeeldingen worden getoond als achtergrond in je profiel header met een cinematic Ken Burns effect.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((index) => {
                  const currentBg = cardsData.biografie?.heroBackgrounds?.[index] || '';
                  return (
                    <div key={index} className="relative">
                      <div className="aspect-video rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center">
                        {currentBg ? (
                          <>
                            <img src={currentBg} alt={`Hero ${index + 1}`} className="w-full h-full object-cover" />
                            <button
                              onClick={() => removeHeroBackground(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 shadow-md"
                            >
                              ×
                            </button>
                          </>
                        ) : (
                          <label className="cursor-pointer text-center p-3 w-full h-full flex flex-col items-center justify-center hover:bg-gray-100 transition-colors">
                            <input
                              type="file"
                              accept="image/png, image/jpeg, image/gif, image/webp"
                              className="hidden"
                              onChange={(e) => handleHeroBackgroundUpload(e.target.files?.[0], index)}
                              disabled={uploadingImage}
                            />
                            {uploadingImage ? (
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                            ) : (
                              <>
                                <div className="text-3xl mb-1">📷</div>
                                <div className="text-xs text-gray-500">Afbeelding {index + 1}</div>
                              </>
                            )}
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Biografie Specials */}
          {activeCard === 'biografie' && (
            <div className="bg-white rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold mb-5 text-purple-600">
                🎯 Expertise & Interesses & Talen
              </h3>

              {/* Expertise */}
              <div className="mb-6">
                <span className="font-medium mr-3 min-w-16">🎯 Expertise</span>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(cardsData.biografie.expertise || []).map((item, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs cursor-pointer hover:bg-blue-200 transition-colors"
                      onClick={() => removeExpertise(index)}
                    >
                      {item} ×
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newExpertise}
                    onChange={(e) => setNewExpertise(e.target.value)}
                    placeholder="Nieuwe expertise"
                    className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && addExpertise()}
                  />
                  <button
                    onClick={addExpertise}
                    className="px-3 py-1.5 bg-blue-600 text-white border-none rounded text-xs cursor-pointer hover:bg-blue-700 transition-colors"
                  >
                    + Toevoegen
                  </button>
                </div>
              </div>

              {/* Interesses */}
              <div className="mb-6">
                <span className="font-medium mr-3 min-w-16">❤️ Interesses</span>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(cardsData.biografie.interesses || []).map((item, index) => (
                    <span
                      key={index}
                      className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => removeInteresse(index)}
                    >
                      {item} ×
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newInteresse}
                    onChange={(e) => setNewInteresse(e.target.value)}
                    placeholder="Nieuwe interesse"
                    className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && addInteresse()}
                  />
                  <button
                    onClick={addInteresse}
                    className="px-3 py-1.5 bg-purple-600 text-white border-none rounded text-xs cursor-pointer hover:bg-purple-700 transition-colors"
                  >
                    + Toevoegen
                  </button>
                </div>
              </div>

              {/* Talen */}
              <div className="mb-6">
                <span className="font-medium mr-3 min-w-16">🗣️ Talen</span>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(cardsData.biografie.talen || []).map((item, index) => (
                    <span
                      key={index}
                      className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs cursor-pointer hover:bg-green-200 transition-colors"
                      onClick={() => removeTaal(index)}
                    >
                      {item} ×
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTaal}
                    onChange={(e) => setNewTaal(e.target.value)}
                    placeholder="Nieuwe taal"
                    className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && addTaal()}
                  />
                  <button
                    onClick={addTaal}
                    className="px-3 py-1.5 bg-green-600 text-white border-none rounded text-xs cursor-pointer hover:bg-green-700 transition-colors"
                  >
                    + Toevoegen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Info voor andere kaarten */}
          {activeCard !== 'biografie' && activeCard !== 'mijn-bedrijf' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800">
              ℹ️ <strong>Deze kaart gebruikt standaard velden.</strong> Alleen "Biografie" heeft extra velden voor Expertise/Interesses/Talen en alleen "Mijn bedrijf" heeft Social Media Links.
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex justify-between items-center mt-6 px-6">
            <div className="text-sm text-gray-600">
              Kaart {tabs.findIndex(tab => tab.id === activeCard) + 1} van {tabs.length}: <strong>{currentCard.title}</strong>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={saveCardsToFirebase}
                disabled={saving}
                className="px-5 py-2.5 text-white border-none rounded text-sm cursor-pointer font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: getCardColor(activeCard) }}
              >
                {saving ? 'Opslaan...' : 'Alles Opslaan'}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DragDropCardsEditor;